import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Phone, MessageSquare, 
  Info, Navigation,
  Wifi, MapPin, Heart, Share2, 
  ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { Card } from '../components/ui/card';
import ContactSection from '../components/property/ContactSection';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import MainPropertySection from '../components/property/MainPropertySection';
import CharacteristicsSection from '../components/property/CharacteristicsSection';
import LocationSection from '../components/property/LocationSection';
import { useVisitTracker } from '../hooks/useVisitTracker';
import { ScrollRevealSection, ScrollReveal } from '../components/property/ScrollReveal';
import ScrollButton from '../components/property/ScrollButton';
import axios from 'axios';

// Custom hook for scrolling
const useScrollObserver = () => {
  const [scrollDirection, setScrollDirection] = useState('down');
  const [scrollProgress, setScrollProgress] = useState(0);
  const prevScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine scroll direction
      if (currentScrollY > prevScrollY.current) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      
      // Calculate scroll progress (0-100%)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(100, Math.max(0, (currentScrollY / scrollHeight) * 100));
      setScrollProgress(progress);
      
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { scrollDirection, scrollProgress };
};

// Helper function for smooth scrolling
const scrollToElement = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  // Calculate the element's position
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  
  // Smooth scroll to the element
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

const PropertyDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  // Estados
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllCharacteristics, setShowAllCharacteristics] = useState(false);
  const [isShowingFlyer, setIsShowingFlyer] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [activeSection, setActiveSection] = useState('main');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isSharing, setIsSharing] = useState(false); // Nuevo estado para prevenir shares múltiples

  // Referencias para secciones
  const mainRef = useRef(null);
  const aboutRef = useRef(null);
  const characRef = useRef(null);
  const locationRef = useRef(null);
  const contactRef = useRef(null);

  // Custom hooks
  const { elapsedTime } = useVisitTracker(property?.id);
  const { scrollDirection, scrollProgress } = useScrollObserver();

  // Determinar si es un alquiler
  const isRental = location.pathname.includes('/alquiler/');

  // Log de montaje del componente
  useEffect(() => {
    console.log('PropertyDetails mounted', { 
      propertyId: id,
      isRental
    });

    // Add smooth scrolling to all anchors
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href').substring(1);
        scrollToElement(targetId, 80);
      });
    });

    return () => {
      // console.log('PropertyDetails unmounted', { propertyId: id });
    };
  }, []);

  // Intersección observador para determinar sección activa
  useEffect(() => {
    if (!property) return;

    const options = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.2
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observe each section
    const sections = ['main', 'sobre', 'caracteristicas', 'ubicacion', 'contacto'];
    sections.forEach(id => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.unobserve(element);
      });
    };
  }, [property]);

  // Scrolling hacia seccion específica con efecto de centrado mejorado
  const scrollToSection = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    // Calcular la posición para centrar la sección de manera más precisa
    const viewportHeight = window.innerHeight;
    const elementRect = section.getBoundingClientRect();
    const elementTop = elementRect.top + window.pageYOffset;
    const elementHeight = elementRect.height;
    
    // Calcular offset para centrar, con límites
    let offset = Math.max(80, (viewportHeight - elementHeight) / 2);
    
    // Asegurar que no se vaya más allá del inicio o final del documento
    const maxScroll = document.documentElement.scrollHeight - viewportHeight;
    const targetScroll = Math.max(0, Math.min(maxScroll, elementTop - offset));
    
    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }, []);

  // Función para obtener detalles de la propiedad
  const fetchPropertyDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
  
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app/api';
      const endpoint = isRental 
        ? `${baseUrl}/alquileres/${id}` 
        : `${baseUrl}/propiedades/${id}`;

      const response = await axios.get(endpoint, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      // Las imágenes ya vienen con URLs completas de Cloudflare
      const propertyData = response.data;
      setProperty(propertyData);
      
      // Verificar estado de favoritos si hay usuario
      if (user && token) {
        await checkFavoriteStatus();
      }
      
    } catch (error) {
      console.error('Error fetching property:', error);
      setError(error.response?.data?.error || 'No se pudo cargar la propiedad');
      toast({
        title: "Error",
        description: "No se pudo cargar la propiedad",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [id, isRental, token, toast, user]);

  // Verificar estado de favoritos
  const checkFavoriteStatus = useCallback(async () => {
    if (!user || !token) {
      setIsFavorite(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://backend-production-1786.up.railway.app/api/favoritos/check/${id}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
      toast({
        title: "Error",
        description: "No se pudo verificar el estado de favoritos",
        variant: "destructive",
      });
    }
  }, [id, user, token, toast]);

  // Manejar favoritos
  const handleFavorite = async () => {
    if (!user) {
      toast({
        title: "Inicia sesión",
        description: "Necesitas iniciar sesión para guardar favoritos",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    if (isLoadingFavorite) return;

    setIsLoadingFavorite(true);

    try {
      if (isFavorite) {
        await axios.delete(`https://backend-production-1786.up.railway.app/api/favoritos/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast({
          title: "Eliminado de favoritos",
          description: "La propiedad se eliminó de tus favoritos",
        });
        setIsFavorite(false);
      } else {
        await axios.post(`https://backend-production-1786.up.railway.app/api/favoritos/${id}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast({
          title: "Añadido a favoritos",
          description: "La propiedad se añadió a tus favoritos",
        });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar favoritos",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFavorite(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchPropertyDetails();
  }, [fetchPropertyDetails]);

  // Funciones auxiliares
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleNextImage = () => {
    if (property?.imagenes?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === property.imagenes.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (property?.imagenes?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? property.imagenes.length - 1 : prev - 1
      );
    }
  };

  // Función de compartir mejorada con prevención de llamadas múltiples
  const handleShare = useCallback(async () => {
    if (!property || isSharing) return;

    setIsSharing(true);

    try {
      // Verificar si hay una operación de compartir en progreso
      if (window._shareInProgress) {
        console.log('Share already in progress, skipping...');
        return;
      }

      window._shareInProgress = true;

      // Usar un método más simple y confiable
      const shareText = `Mira esta ${isRental ? 'alquiler' : 'propiedad'} en InKayros: ${property.titulo} - ${window.location.href}`;

      // Intentar compartir nativo primero (solo en dispositivos que lo soporten)
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        try {
          await navigator.share({
            title: property.titulo,
            text: shareText,
            url: window.location.href
          });
          return;
        } catch (shareError) {
          // Si el usuario cancela o hay error, continuar con fallback
          if (shareError.name === 'AbortError') {
            console.log('User cancelled share');
            return;
          }
          console.log('Native share failed, using fallback:', shareError);
        }
      }

      // Fallback: Usar el método de texto oculto más confiable
      const fallbackCopy = () => {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = window.location.href;
          textArea.style.position = 'absolute';
          textArea.style.left = '-9999px';
          textArea.style.top = '-9999px';
          textArea.style.opacity = '0';
          textArea.setAttribute('readonly', '');
          textArea.setAttribute('aria-hidden', 'true');
          
          document.body.appendChild(textArea);
          
          // Seleccionar el texto
          textArea.select();
          textArea.setSelectionRange(0, 99999); // Para móviles
          
          // Intentar copiar
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
          } else {
            throw new Error('Copy command failed');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
          // Mostrar diálogo para copiar manualmente
          const promptText = `Copia este enlace:\n\n${window.location.href}`;
          if (window.prompt) {
            window.prompt(promptText, window.location.href);
          } else {
            alert(promptText);
          }
        }
      };

      // Intentar clipboard API moderno si está disponible
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(window.location.href);
          toast({
            title: "Link copiado",
            description: "El enlace se ha copiado al portapapeles",
          });
        } catch (clipboardError) {
          console.log('Modern clipboard failed, using fallback:', clipboardError);
          fallbackCopy();
        }
      } else {
        // Usar fallback directamente
        fallbackCopy();
      }

    } catch (error) {
      console.error('Share function error:', error);
      
      toast({
        title: "Información",
        description: "Puedes copiar el enlace desde la barra de direcciones de tu navegador",
        variant: "default",
      });
    } finally {
      // Limpiar flags
      window._shareInProgress = false;
      
      // Reset del estado después de un delay
      setTimeout(() => {
        setIsSharing(false);
      }, 2000);
    }
  }, [property, isSharing, isRental, toast]);

  // Exponer la función de compartir globalmente para el componente hijo
  useEffect(() => {
    window.handleShareFromParent = handleShare;
    return () => {
      delete window.handleShareFromParent;
    };
  }, [handleShare]);

  // Estados de carga y error
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex justify-center items-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex justify-center items-center">
        <div className="text-center px-4">
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-2xl shadow-xl border border-gray-100 dark:border-white/10 p-8 max-w-md mx-auto">
            <div className="bg-red-50 dark:bg-red-500/20 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center">
              <X className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error al cargar la propiedad</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">{error}</p>
            <button 
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex justify-center items-center">
        <div className="text-center px-4">
          <div className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-2xl shadow-xl border border-gray-100 dark:border-white/10 p-8 max-w-md mx-auto">
            <div className="bg-gray-50 dark:bg-gray-500/20 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-gray-500 dark:text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-600 dark:text-gray-300 mb-4">Propiedad no encontrada</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
              No pudimos encontrar la propiedad que buscas.
            </p>
            <button 
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Scroll Button */}
      <ScrollButton />

      <div className="relative">
        {/* Main Section */}
        <div id="main">
          <MainPropertySection 
            property={property}
            formatPrice={formatPrice}
            handleContact={() => scrollToSection('contacto')}
            currentImageIndex={currentImageIndex}
            handleFavorite={handleFavorite}
            handleShare={handleShare}
            handlePrevImage={handlePrevImage}
            handleNextImage={handleNextImage}
            setCurrentImageIndex={setCurrentImageIndex}
            isFavorite={isFavorite}
            showFlyer={() => setIsShowingFlyer(true)}
            isRental={isRental}
          />
        </div>

        {/* About Section */}
        <div id="sobre" className="min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={30}
            duration={0.6}
          >
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 w-full">
              <Card className="backdrop-blur-xl bg-white/80 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-4 sm:p-6 lg:p-8 mx-2 sm:mx-0 transform transition-all duration-500 hover:shadow-2xl dark:hover:shadow-black/50 overflow-hidden">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-500/20 p-2 rounded-xl">
                    <Info className="w-6 h-6 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
                    Sobre el Inmueble
                  </h2>
                </div>
                
                {/* Container con mejor manejo del texto */}
                <div className="space-y-4">
                  <div className="prose prose-sm sm:prose-lg max-w-none">
                    <div className="text-gray-700 dark:text-gray-300 leading-relaxed break-words hyphens-auto text-sm sm:text-base lg:text-lg whitespace-pre-line">
                      {showFullDescription 
                        ? (property.descripcion_corta || 'No hay descripción disponible')
                        : (property.descripcion_corta && property.descripcion_corta.length > 300 
                          ? `${property.descripcion_corta.substring(0, 300)}...`
                          : (property.descripcion_corta || 'No hay descripción disponible')
                        )
                      }
                    </div>
                  </div>
                  
                  {/* Si la descripción es muy larga, agregar opción de expandir */}
                  {property.descripcion_corta && property.descripcion_corta.length > 300 && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base"
                      >
                        {showFullDescription ? 'Ver menos' : 'Ver más'}
                        <ChevronRight className={`w-4 h-4 transform transition-transform duration-200 ${showFullDescription ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 sm:mt-8 pt-4 border-t border-gray-100 dark:border-white/10">
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                    <span>Información actualizada</span>
                  </div>
                </div>
              </Card>
            </div>
          </ScrollRevealSection>
        </div>

        {/* Characteristics Section */}
        <div id="caracteristicas" className="min-h-[90vh] flex items-center justify-center py-8 sm:py-16">
          <div className="w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <CharacteristicsSection 
                property={property}
                showAll={showAllCharacteristics}
                toggleShowAll={() => setShowAllCharacteristics(!showAllCharacteristics)}
                isRental={isRental}
              />
            </div>
          </div>
        </div>

        {/* Location Section */}
        <div id="ubicacion" className="min-h-screen flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="right"
            distance={50}
            duration={0.7}
          >
            <LocationSection property={property} />
          </ScrollRevealSection>
        </div>

        {/* Contact Section */}
        <div id="contacto" className="min-h-screen flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={50}
            duration={0.7}
          >
            <ContactSection 
              property={property} 
              handleContact={() => {}}
              isRental={isRental}
            />
          </ScrollRevealSection>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;